<template>
  <div
    v-if="videoUrl || imageUrl"
    class="group relative aspect-[16/9] overflow-hidden"
  >
    <div class="absolute -bottom-2 h-2 w-full bg-primary-950 blur-2xl" />

    <div v-if="videoUrl" class="absolute inset-0">
      <app-video
        autoplay
        :preview="preview"
        :src="videoUrl"
        @timeupdate="onVideoTimeUpdate"
      />

      <app-fade-transition>
        <div
          v-show="preview"
          class="absolute inset-0 grid cursor-pointer place-content-center transition group-hover:bg-black/30"
          tabindex="0"
          @click="onPlayClick"
        >
          <app-button
            class="scale-95 opacity-0 transition group-hover:scale-100 group-hover:opacity-100"
            color="light"
            icon="ph:play-circle"
            size="lg"
            tabindex="-1"
          >
            {{ $t("course.page.watch_full_video_label") }}
          </app-button>
        </div>
      </app-fade-transition>
    </div>

    <app-image
      v-else-if="imageUrl"
      alt=""
      class="h-full w-full object-cover"
      :height="1080"
      :src="imageUrl"
      :width="1920"
    />
  </div>
</template>

<script lang="ts" setup>
const properties = defineProps<{
  course: Pick<DatabaseTable<"courses">, "video_url" | "cover_url"> & {
    teacher: Pick<DatabaseTable<"teachers">, "cover_url" | "full_name">;
  };
}>();

const { $analytics } = useNuxtApp();

const preview = ref(true);

const onPlayClick = () => {
  preview.value = false;
  $analytics.track("course_video_played");
};

const videoProgressInPercent = ref(0);
const TRACKED_PROGRESS_STEPS = [25, 50, 75] as const;

TRACKED_PROGRESS_STEPS.forEach((step) => {
  whenever(
    () => videoProgressInPercent.value > step,
    () => {
      $analytics.track("course_video_watched", { progress_in_percent: step });
    },
  );
});

const onVideoTimeUpdate = ({
  currentTime,
  totalTime,
}: {
  currentTime: number;
  totalTime: number;
}) => {
  videoProgressInPercent.value = Math.floor((currentTime * 100) / totalTime);
};

const videoUrl = useCloudinaryVideo(properties.course.video_url);
const imageUrl = computed(
  () => properties.course.cover_url ?? properties.course.teacher.cover_url,
);
</script>
